'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function DebtPayoffCalculator() {
  const t = useTranslations("tools.debtPayoffCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Debt state
  const [debts, setDebts] = useState([
    { id: 0, name: 'Credit Card 1', balance: 5000, payment: 150, rate: 18.99 },
    { id: 1, name: 'Auto Loan', balance: 15000, payment: 350, rate: 5.5 }
  ]);
  const [nextId, setNextId] = useState(2);

  // Extra payments
  const [extraMonthly, setExtraMonthly] = useState(0);
  const [extraYearly, setExtraYearly] = useState(0);

  // Strategy
  const [strategy, setStrategy] = useState('avalanche');

  // Results
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  // Screen size detection
  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  // Responsive style helper
  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  // Add debt
  const addDebt = () => {
    setDebts([...debts, { id: nextId, name: '', balance: 0, payment: 0, rate: 0 }]);
    setNextId(nextId + 1);
  };

  // Remove debt
  const removeDebt = (id: number) => {
    if (debts.length > 1) {
      setDebts(debts.filter(d => d.id !== id));
    }
  };

  // Update debt
  const updateDebt = (id: number, field: string, value: any) => {
    setDebts(debts.map(d => d.id === id ? { ...d, [field]: value } : d));
  };

  // Calculate payoff
  const calculatePayoff = (debtsData: any[], extraMonthly: number, extraYearly: number, strategy: string) => {
    const sortedDebts = [...debtsData];

    if (strategy === 'avalanche') {
      sortedDebts.sort((a, b) => b.rate - a.rate);
    } else {
      sortedDebts.sort((a, b) => a.balance - b.balance);
    }

    const schedule = [];
    let month = 0;
    let totalInterest = 0;
    const activeDebts = sortedDebts.map(d => ({ ...d, originalBalance: d.balance }));

    while (activeDebts.some(d => d.balance > 0)) {
      month++;
      let monthlyPayment = 0;
      let monthlyPrincipal = 0;
      let monthlyInterest = 0;

      const extraThisMonth = extraMonthly + (month % 12 === 0 ? extraYearly : 0);

      activeDebts.forEach(debt => {
        if (debt.balance > 0) {
          const monthlyRate = debt.rate / 100 / 12;
          const interest = debt.balance * monthlyRate;
          monthlyInterest += interest;

          let payment = debt.payment;
          let principal = payment - interest;

          if (principal > debt.balance) {
            principal = debt.balance;
            payment = principal + interest;
          }

          debt.balance -= principal;
          monthlyPayment += payment;
          monthlyPrincipal += principal;
          totalInterest += interest;
        }
      });

      if (extraThisMonth > 0) {
        let remainingExtra = extraThisMonth;
        for (const debt of activeDebts) {
          if (debt.balance > 0 && remainingExtra > 0) {
            const extraApplied = Math.min(remainingExtra, debt.balance);
            debt.balance -= extraApplied;
            remainingExtra -= extraApplied;
            monthlyPrincipal += extraApplied;
            monthlyPayment += extraApplied;
          }
        }
      }

      const totalRemaining = activeDebts.reduce((sum, d) => sum + d.balance, 0);

      schedule.push({
        month,
        payment: monthlyPayment,
        principal: monthlyPrincipal,
        interest: monthlyInterest,
        balance: totalRemaining
      });

      if (month > 600) break;
    }

    return { schedule, totalInterest, months: month, sortedDebts: activeDebts };
  };

  // Handle calculate
  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const validDebts = debts.filter(d => d.balance > 0 && d.payment > 0);

    if (validDebts.length === 0) {
      setError(t('errorNoDebts'));
      setResults(null);
      return;
    }

    // Validate minimum payments
    for (const debt of validDebts) {
      const monthlyRate = debt.rate / 100 / 12;
      const minInterest = debt.balance * monthlyRate;
      if (debt.payment <= minInterest) {
        setError(t('errorMinPayment'));
        setResults(null);
        return;
      }
    }

    const totalDebt = validDebts.reduce((sum, d) => sum + d.balance, 0);
    const result = calculatePayoff(validDebts, extraMonthly, extraYearly, strategy);

    setResults({
      totalDebt,
      months: result.months,
      totalInterest: result.totalInterest,
      totalPaid: totalDebt + result.totalInterest,
      schedule: result.schedule,
      sortedDebts: result.sortedDebts
    });
  };

  // Colors
  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  // Styles
  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem' },
    { padding: '3rem 1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    {
      maxWidth: '1000px',
      margin: '0 auto',
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const toolHeaderStyle = {
    textAlign: 'center' as const,
    marginBottom: '2rem'
  };

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const debtsHeaderStyle = screenSize === 'mobile' ? { display: 'none' } : {
    display: 'grid',
    gridTemplateColumns: screenSize === 'tablet' ? '30px 1fr 120px 120px 100px 40px' : '30px 1fr 140px 140px 120px 40px',
    gap: '0.75rem',
    padding: '0.75rem',
    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
    color: 'white',
    borderRadius: '0.5rem',
    fontWeight: 600,
    fontSize: '0.875rem',
    marginBottom: '1rem'
  };

  const debtRowStyle = screenSize === 'mobile' ? {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem',
    padding: '1rem',
    background: '#f8fafc',
    borderRadius: '0.5rem',
    marginBottom: '0.75rem',
    border: '1px solid #e2e8f0'
  } : {
    display: 'grid',
    gridTemplateColumns: screenSize === 'tablet' ? '30px 1fr 120px 120px 100px 40px' : '30px 1fr 140px 140px 120px 40px',
    gap: '0.75rem',
    alignItems: 'center',
    padding: '0.75rem',
    background: '#f8fafc',
    borderRadius: '0.5rem',
    marginBottom: '0.75rem',
    transition: 'all 0.3s ease'
  };

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '0.875rem',
      transition: 'all 0.3s ease',
      outline: 'none'
    },
    { padding: '0.625rem' },
    { padding: '0.625rem' },
    { padding: '0.625rem' }
  );

  const removeBtnStyle = {
    background: '#ef4444',
    color: 'white',
    border: 'none',
    width: '32px',
    height: '32px',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '1.25rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease'
  };

  const addDebtBtnStyle = {
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: 'white',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.875rem',
    transition: 'all 0.3s ease',
    marginBottom: '1.5rem',
    width: screenSize === 'mobile' ? '100%' : 'auto'
  };

  const extraPaymentsStyle = {
    background: '#fef3c7',
    padding: '1.5rem',
    borderRadius: '0.75rem',
    marginBottom: '1.5rem'
  };

  const extraPaymentRowStyle = screenSize === 'mobile' ? {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem',
    marginBottom: '0.75rem'
  } : {
    display: 'grid',
    gridTemplateColumns: '140px 1fr',
    gap: '1rem',
    alignItems: 'center',
    marginBottom: '0.75rem'
  };

  const strategySectionStyle = {
    background: '#f0fdf4',
    padding: '1.5rem',
    borderRadius: '0.75rem',
    marginBottom: '1.5rem'
  };

  const radioGroupStyle = screenSize === 'mobile' ? {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1rem'
  } : {
    display: 'flex',
    gap: '2rem'
  };

  const radioLabelStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
    fontSize: '0.9375rem'
  };

  const calculateBtnStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.75rem',
      cursor: 'pointer',
      fontWeight: 700,
      width: '100%',
      transition: 'all 0.3s ease',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px'
    },
    { padding: '1rem 2rem', fontSize: '1rem' },
    { padding: '1rem 2.5rem', fontSize: '1.125rem' },
    { padding: '1rem 3rem', fontSize: '1.125rem' }
  );

  const resultsStyle = {
    marginTop: '2rem',
    padding: '2rem',
    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
    borderRadius: '1rem',
    border: '3px solid #f59e0b'
  };

  const summaryGridStyle = screenSize === 'mobile' ? {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '1rem',
    marginBottom: '2rem'
  } : {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
    marginBottom: '2rem'
  };

  const summaryCardStyle = {
    background: 'white',
    padding: '1.25rem',
    borderRadius: '0.75rem',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = {
    ...paragraphStyle,
    paddingLeft: '1.5rem'
  };

  const errorStyle = {
    background: '#fee2e2',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginBottom: '1rem',
    borderLeft: '4px solid #dc2626'
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>{t('toolTitle')}</h2>
          </div>

          <form onSubmit={handleCalculate}>
            {error && (
              <div style={errorStyle}>{error}</div>
            )}

            <div style={{ marginBottom: '2rem' }}>
              {screenSize !== 'mobile' && (
                <div style={debtsHeaderStyle}>
                  <div>#</div>
                  <div>{t('debtName')}</div>
                  <div>{t('balance')}</div>
                  <div>{t('minPayment')}</div>
                  <div>{t('interestRate')}</div>
                  <div></div>
                </div>
              )}

              <div>
                {debts.map((debt, index) => (
                  <div key={debt.id} style={debtRowStyle}>
                    {screenSize === 'mobile' && (
                      <div style={{ fontWeight: 600, color: '#64748b', marginBottom: '0.5rem' }}>
                        {t('debtNumber', { number: index + 1 })}
                      </div>
                    )}
                    {screenSize !== 'mobile' && (
                      <div style={{ fontWeight: 600, color: '#64748b' }}>{index + 1}.</div>
                    )}
                    <input
                      type="text"
                      style={inputStyle}
                      placeholder={t('debtNamePlaceholder')}
                      value={debt.name}
                      onChange={(e) => updateDebt(debt.id, 'name', e.target.value)}
                    />
                    <input
                      type="number"
                      style={inputStyle}
                      placeholder={t('balancePlaceholder')}
                      value={debt.balance || ''}
                      onChange={(e) => updateDebt(debt.id, 'balance', parseFloat(e.target.value) || 0)}
                      step="0.01"
                      min="0"
                      required
                    />
                    <input
                      type="number"
                      style={inputStyle}
                      placeholder={t('minPaymentPlaceholder')}
                      value={debt.payment || ''}
                      onChange={(e) => updateDebt(debt.id, 'payment', parseFloat(e.target.value) || 0)}
                      step="0.01"
                      min="0"
                      required
                    />
                    <input
                      type="number"
                      style={inputStyle}
                      placeholder={t('interestRatePlaceholder')}
                      value={debt.rate || ''}
                      onChange={(e) => updateDebt(debt.id, 'rate', parseFloat(e.target.value) || 0)}
                      step="0.01"
                      min="0"
                      max="100"
                      required
                    />
                    <button
                      type="button"
                      style={removeBtnStyle}
                      onClick={() => removeDebt(debt.id)}
                      disabled={debts.length === 1}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>

              <button type="button" style={addDebtBtnStyle} onClick={addDebt}>
                {t('addDebtButton')}
              </button>
            </div>

            <div style={extraPaymentsStyle}>
              <h3 style={{ color: '#92400e', fontSize: '1.125rem', marginBottom: '1rem' }}>
                {t('extraPaymentsTitle')}
              </h3>
              <div style={extraPaymentRowStyle}>
                <input
                  type="number"
                  style={inputStyle}
                  placeholder="0"
                  value={extraMonthly || ''}
                  onChange={(e) => setExtraMonthly(parseFloat(e.target.value) || 0)}
                  step="0.01"
                  min="0"
                />
                <label>{t('extraMonthlyLabel')}</label>
              </div>
              <div style={extraPaymentRowStyle}>
                <input
                  type="number"
                  style={inputStyle}
                  placeholder="0"
                  value={extraYearly || ''}
                  onChange={(e) => setExtraYearly(parseFloat(e.target.value) || 0)}
                  step="0.01"
                  min="0"
                />
                <label>{t('extraYearlyLabel')}</label>
              </div>
            </div>

            <div style={strategySectionStyle}>
              <h3 style={{ color: '#166534', fontSize: '1.125rem', marginBottom: '1rem' }}>
                {t('strategyTitle')}
              </h3>
              <div style={radioGroupStyle}>
                <label style={radioLabelStyle}>
                  <input
                    type="radio"
                    name="strategy"
                    value="avalanche"
                    checked={strategy === 'avalanche'}
                    onChange={(e) => setStrategy(e.target.value)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span><strong>{t('avalancheTitle')}</strong> - {t('avalancheDesc')}</span>
                </label>
                <label style={radioLabelStyle}>
                  <input
                    type="radio"
                    name="strategy"
                    value="snowball"
                    checked={strategy === 'snowball'}
                    onChange={(e) => setStrategy(e.target.value)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span><strong>{t('snowballTitle')}</strong> - {t('snowballDesc')}</span>
                </label>
              </div>
            </div>

            <button type="submit" style={calculateBtnStyle}>
              {t('calculateButton')}
            </button>
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={{ color: '#92400e', fontSize: '1.5rem', marginBottom: '1.5rem', textAlign: 'center' }}>
                {t('resultsTitle')}
              </h3>

              <div style={summaryGridStyle}>
                <div style={summaryCardStyle}>
                  <div style={{ color: '#64748b', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('totalDebtLabel')}
                  </div>
                  <div style={{ color: '#1e293b', fontSize: '1.75rem', fontWeight: 700 }}>
                    ${results.totalDebt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
                <div style={summaryCardStyle}>
                  <div style={{ color: '#64748b', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('timeToPayoffLabel')}
                  </div>
                  <div style={{ color: '#1e293b', fontSize: '1.75rem', fontWeight: 700 }}>
                    {(() => {
                      const years = Math.floor(results.months / 12);
                      const months = results.months % 12;
                      let timeText = '';
                      if (years > 0) timeText += years + (years === 1 ? ' yr ' : ' yrs ');
                      if (months > 0) timeText += months + (months === 1 ? ' mo' : ' mos');
                      return timeText || '0 mos';
                    })()}
                  </div>
                </div>
                <div style={summaryCardStyle}>
                  <div style={{ color: '#64748b', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('totalInterestLabel')}
                  </div>
                  <div style={{ color: '#1e293b', fontSize: '1.75rem', fontWeight: 700 }}>
                    ${results.totalInterest.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
                <div style={summaryCardStyle}>
                  <div style={{ color: '#64748b', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('totalPaidLabel')}
                  </div>
                  <div style={{ color: '#1e293b', fontSize: '1.75rem', fontWeight: 700 }}>
                    ${results.totalPaid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              </div>

              <div style={{ background: 'white', padding: '1.5rem', borderRadius: '0.75rem', marginBottom: '1.5rem' }}>
                <h4 style={{ color: '#1e293b', fontSize: '1.125rem', marginBottom: '1rem' }}>
                  {t('payoffOrderTitle')}
                </h4>
                {results.sortedDebts.map((debt: any, index: number) => (
                  <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: '#f8fafc', borderRadius: '0.5rem', marginBottom: '0.5rem', borderLeft: '4px solid #2563eb', flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontWeight: 600, color: '#1e293b' }}>
                        {index + 1}. {debt.name}
                      </div>
                      <div style={{ color: '#64748b', fontSize: '0.875rem' }}>
                        {t('balanceLabel')}: ${debt.originalBalance.toLocaleString()} | {t('rateLabel')}: {debt.rate}%
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ background: 'white', padding: '1.5rem', borderRadius: '0.75rem', overflowX: 'auto' }}>
                <h4 style={{ color: '#1e293b', fontSize: '1.125rem', marginBottom: '1rem' }}>
                  {t('scheduleTitle')}
                </h4>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr>
                      <th style={{ background: '#f1f5f9', padding: '0.75rem', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0' }}>{t('monthColumn')}</th>
                      <th style={{ background: '#f1f5f9', padding: '0.75rem', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0' }}>{t('paymentColumn')}</th>
                      <th style={{ background: '#f1f5f9', padding: '0.75rem', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0' }}>{t('principalColumn')}</th>
                      <th style={{ background: '#f1f5f9', padding: '0.75rem', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0' }}>{t('interestColumn')}</th>
                      <th style={{ background: '#f1f5f9', padding: '0.75rem', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0' }}>{t('remainingColumn')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.schedule.slice(0, 12).map((s: any, i: number) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc' }}>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #f1f5f9' }}>{s.month}</td>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #f1f5f9' }}>${s.payment.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #f1f5f9' }}>${s.principal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #f1f5f9' }}>${s.interest.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #f1f5f9' }}>${s.balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep3')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep4')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep5')}</li>
        </ol>
      </section>

      {/* Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <h3 style={{ ...h2Style, fontSize: '1.25rem', marginTop: '1.5rem' }}>{t('insightsSubtitle1')}</h3>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>

        <h3 style={{ ...h2Style, fontSize: '1.25rem', marginTop: '1.5rem' }}>{t('insightsSubtitle2')}</h3>
        <p style={paragraphStyle}>{t('insightsParagraph2')}</p>

        <h3 style={{ ...h2Style, fontSize: '1.25rem', marginTop: '1.5rem' }}>{t('insightsSubtitle3')}</h3>
        <p style={paragraphStyle}>{t('insightsParagraph3')}</p>

        <h3 style={{ ...h2Style, fontSize: '1.25rem', marginTop: '1.5rem' }}>{t('insightsSubtitle4')}</h3>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.5rem' }}>{t('insightsBullet1')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('insightsBullet2')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('insightsBullet3')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('insightsBullet4')}</li>
        </ul>
      </section>

      {/* Details */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>

        <h3 style={{ ...h2Style, fontSize: '1.25rem', marginTop: '1.5rem' }}>{t('detailsSubtitle1')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph1')}</p>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.5rem' }}><strong>{t('avalancheTitle')}:</strong> {t('detailsAvalanche')}</li>
          <li style={{ marginBottom: '0.5rem' }}><strong>{t('snowballTitle')}:</strong> {t('detailsSnowball')}</li>
        </ul>

        <h3 style={{ ...h2Style, fontSize: '1.25rem', marginTop: '1.5rem' }}>{t('detailsSubtitle2')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph2')}</p>

        <h3 style={{ ...h2Style, fontSize: '1.25rem', marginTop: '1.5rem' }}>{t('detailsSubtitle3')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph3')}</p>

        <h3 style={{ ...h2Style, fontSize: '1.25rem', marginTop: '1.5rem' }}>{t('detailsSubtitle4')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph4')}</p>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <h3 style={{ ...h2Style, fontSize: '1.125rem', marginTop: '1.5rem' }}>{t('faqQuestion1')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer1')}</p>

        <h3 style={{ ...h2Style, fontSize: '1.125rem', marginTop: '1.5rem' }}>{t('faqQuestion2')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer2')}</p>

        <h3 style={{ ...h2Style, fontSize: '1.125rem', marginTop: '1.5rem' }}>{t('faqQuestion3')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer3')}</p>

        <h3 style={{ ...h2Style, fontSize: '1.125rem', marginTop: '1.5rem' }}>{t('faqQuestion4')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer4')}</p>

        <h3 style={{ ...h2Style, fontSize: '1.125rem', marginTop: '1.5rem' }}>{t('faqQuestion5')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer5')}</p>

        <h3 style={{ ...h2Style, fontSize: '1.125rem', marginTop: '1.5rem' }}>{t('faqQuestion6')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer6')}</p>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://bountisphere.com/blog/best-debt-payoff-calculators-2025" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              Best Debt Payoff Calculators for 2025: Snowball vs. Avalanche
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.debtpayoffplanner.com/debt-snowball-calculator/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              Debt Snowball Calculator
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.bankrate.com/personal-finance/debt/debt-payoff-calculator/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              Debt Paydown Calculator - Bankrate
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://pocketguard.com/debt-payoff-calculator/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              Debt Payoff Calculator - PocketGuard
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.sofi.com/learn/content/debt-snowball-payoff-calculator/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              2025 Debt Snowball Payoff Calculation - SoFi
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}